import { FC, useContext } from "react";

import { Box, Stack, useTheme } from "@mui/material";
import { FormBackgroundTheme } from "@simplyk/common";

import {
  ORG_PAGE_DATED_EVENTS_ID,
  ORG_PAGE_NEWSLETTERS_ID,
  ORG_PAGE_SUPPORT_FORMS_ID,
  // eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
} from "../../../dashboard/features/LiveOrgPageEditor/common/useScrollOrgPagePreview";

import { ShapesProvider } from "@/canvas/components/BackgroundShapes/ShapesContext";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- the sticky Zeffy logo sits below the owner banner, whose height lives in this dashboard context
import { NotificationBannerHeightContext } from "@/components/NotificationBanners/NotificationBannerHeightContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormDivider } from "@/forms-design-system/FormDivider";
import { FormTypography } from "@/forms-design-system/FormTypography";

type OrganizationPageLayoutProps = {
  backgroundTheme?: FormBackgroundTheme | null;
  brandColorHex?: string;
  hasOverlay?: boolean;
  slots: {
    ownerView?: React.ReactNode;
    zeffyLogo: React.ReactNode;
    banner: React.ReactNode;
    header: React.ReactNode;
    description: React.ReactNode;
    events: React.ReactNode;
    otherCampaigns: React.ReactNode;
    newsletters?: React.ReactNode;
    footer: React.ReactNode;
    poweredByZeffy: React.ReactNode;
  };
};

const SECTION_GAP = { xs: 3, md: 5 };

export const OrganizationPageLayout: FC<OrganizationPageLayoutProps> = ({
  backgroundTheme = null,
  brandColorHex,
  hasOverlay = false,
  slots,
}) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const { isExpanded, bannerHeight } = useContext(NotificationBannerHeightContext);
  const ownerBannerOffset = slots.ownerView && isExpanded ? bannerHeight : 0;

  return (
    <ShapesProvider backgroundTheme={backgroundTheme} brandColorHex={brandColorHex}>
      <Stack
        direction="column"
        sx={{
          position: "absolute",
          width: "100%",
          minHeight: "100vh",
          ["@supports (min-height: 100dvh)"]: {
            minHeight: "100dvh",
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: hasOverlay ? "rgba(255, 255, 255, 0.4)" : "transparent",
            zIndex: 200,
            pointerEvents: "none",
            opacity: hasOverlay ? 1 : 0,
            transition: "opacity 0.4s ease-in-out, background-color 0.4s ease-in-out",
          },
        }}
      >
        {slots.ownerView && <>{slots.ownerView}</>}
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            position: "sticky",
            top: (theme) => `calc(${theme.spacing(3)} + ${ownerBannerOffset}px)`,
            transition: "top 0.3s ease-out",
            marginLeft: (theme) => theme.spacing(3),
            alignSelf: "flex-start",
            height: 0,
            zIndex: 10,
          }}
        >
          <Box sx={{ width: "fit-content" }}>{slots.zeffyLogo}</Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(12, 1fr)",
            marginTop: { xs: 2, md: 3 },
            marginLeft: { xs: 2, md: 0 },
            marginRight: { xs: 2, md: 0 },
          }}
          gap={1}
        >
          <Stack sx={{ gridColumn: { xs: "1 / 13", md: "3 / 11" } }} gap={2}>
            {slots.banner && <Box sx={{ width: "100%" }}>{slots.banner}</Box>}

            <Stack
              direction="column"
              sx={{
                paddingX: { xs: 0, md: 6 },
                gap: { xs: 3, md: 2 },
              }}
            >
              {slots.header}
              <Stack direction="column" sx={{ gap: SECTION_GAP }}>
                {slots.description && (
                  <Stack direction="column" sx={{ gap: { xs: 1, md: 2 } }}>
                    <Stack direction="column" gap={1}>
                      <FormTypography
                        sx={{
                          typography: { xs: "body2", sm: "body1" },
                          color: theme.palette.forms.text.moderate,
                          opacity: 0.6,
                        }}
                      >
                        {t("organization", "ourMission")}
                      </FormTypography>

                      <Box>{slots.description}</Box>
                    </Stack>
                  </Stack>
                )}

                {/* Invisible scroll target for events - always present even if section doesn't exist */}
                <SectionScrollTarget id={ORG_PAGE_DATED_EVENTS_ID} />
                {slots.events && <OrganizationPageSectionLayout>{slots.events}</OrganizationPageSectionLayout>}

                {/* Invisible scroll target for support forms - always present even if section doesn't exist */}
                <SectionScrollTarget id={ORG_PAGE_SUPPORT_FORMS_ID} />
                {slots.otherCampaigns && (
                  <OrganizationPageSectionLayout>{slots.otherCampaigns}</OrganizationPageSectionLayout>
                )}

                <SectionScrollTarget id={ORG_PAGE_NEWSLETTERS_ID} />
                {slots.newsletters && (
                  <OrganizationPageSectionLayout>{slots.newsletters}</OrganizationPageSectionLayout>
                )}

                <OrganizationPageSectionLayout>{slots.footer}</OrganizationPageSectionLayout>

                <Stack
                  direction="column"
                  alignItems="center"
                  sx={{ marginTop: { xs: 2, md: 7.5 }, marginBottom: { xs: 5, md: 6 } }}
                >
                  {slots.poweredByZeffy}
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </ShapesProvider>
  );
};

const SectionScrollTarget: FC<{ id: string }> = ({ id }) => (
  <Box
    component="span"
    id={id}
    sx={{
      display: "block",
      marginTop: { xs: -SECTION_GAP.xs, md: -SECTION_GAP.md },
      "&:first-child": { marginTop: 0 },
    }}
  />
);

type OrganizationPageSectionLayoutProps = {
  children: React.ReactNode;
};

const OrganizationPageSectionLayout: FC<OrganizationPageSectionLayoutProps> = ({ children }) => {
  return (
    <>
      <FormDivider />
      <Stack direction="column" gap={1}>
        {children}
      </Stack>
    </>
  );
};
